<template>
  <div class="lukeyun-order-list">
    <h2>路客云消费管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>订单列表</span>
          <div class="header-buttons">
            <el-button type="success" @click="handleSyncOrders('pending')">
              <el-icon><RefreshRight /></el-icon>
              未生效订单同步
            </el-button>
            <el-button type="primary" @click="handleSyncOrders('verified')">
              <el-icon><RefreshRight /></el-icon>
              已生效订单同步
            </el-button>
          </div>
        </div>
      </template>
      <div class="order-list-content">
        <!-- 筛选区域 -->
        <div class="filter-area">
          <el-select
            v-model="filterForm.processStatus"
            placeholder="处理状态"
            style="width: 150px; margin-right: 10px"
            clearable
          >
            <el-option label="待处理" value="pending" />
            <el-option label="已处理" value="processed" />
          </el-select>
          <el-select
            v-model="filterForm.verificationStatus"
            placeholder="核销状态"
            style="width: 150px; margin-right: 10px"
            clearable
          >
            <el-option label="未生效" value="unverified" />
            <el-option label="已生效" value="verified" />
          </el-select>
          <el-input
            v-model="filterForm.keyword"
            placeholder="请输入订单号、手机号"
            prefix-icon="Search"
            clearable
            class="search-input"
          >
            <template #append>
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="orderList"
          stripe
          style="width: 100%"
          class="order-table"
          :row-key="row => row.id"
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="order_no" label="订单号" width="200" />
          <el-table-column prop="virtual_phone" label="虚拟手机号" width="150" />
          <el-table-column prop="real_phone" label="真实手机号" width="150" />
          <el-table-column prop="amount" label="金额" width="120">
            <template #default="scope">
              {{ scope.row.amount }} 元
            </template>
          </el-table-column>
          <el-table-column prop="payment_status" label="支付状态" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.payment_status === 'paid' ? 'success' : 'danger'">
                {{ scope.row.payment_status === 'paid' ? '已支付' : '未支付' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="verification_status" label="核销状态" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.verification_status === 'verified' ? 'success' : 'warning'">
                {{ scope.row.verification_status === 'verified' ? '已生效' : '未生效' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="process_status" label="处理状态" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.process_status === 'processed' ? 'success' : 'info'">
                {{ scope.row.process_status === 'processed' ? '已处理' : '待处理' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="order_date" label="订单日期" width="180" />
          <el-table-column prop="verification_date" label="核销日期" width="180" />
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="handleProcessOrder(scope.row)"
              >
                <el-icon><Edit /></el-icon>
                处理
              </el-button>
              <el-button
                type="warning"
                size="small"
                @click="handleViewDetails(scope.row)"
                style="margin-left: 8px"
              >
                <el-icon><View /></el-icon>
                详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 处理订单弹窗 -->
    <el-dialog
      v-model="processDialogVisible"
      title="处理路客云订单"
      width="50%"
    >
      <el-form :model="processForm" label-width="100px">
        <el-form-item label="订单号">
          <el-input v-model="processForm.order_no" disabled />
        </el-form-item>
        <el-form-item label="虚拟手机号">
          <el-input v-model="processForm.virtual_phone" disabled />
        </el-form-item>
        <el-form-item label="真实手机号" required>
          <el-input v-model="processForm.real_phone" placeholder="请输入真实手机号" />
        </el-form-item>
        <el-form-item label="关联用户">
          <el-select v-model="processForm.user_id" placeholder="请选择用户">
            <el-option
              v-for="user in userList"
              :key="user.id"
              :label="`${user.name} (${user.phone})`"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="processDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitProcessOrder">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Edit, View, RefreshRight } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'LukeyunOrderList',
  components: {
    Search,
    Edit,
    View,
    RefreshRight
  },
  setup() {
    // 加载状态
    const loading = ref(false);
    // 订单列表
    const orderList = ref([]);
    // 分页数据
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0,
    });
    // 筛选表单
    const filterForm = reactive({
      processStatus: '',
      verificationStatus: '',
      keyword: '',
    });
    // 处理订单弹窗
    const processDialogVisible = ref(false);
    // 处理订单表单
    const processForm = reactive({
      id: '',
      order_no: '',
      virtual_phone: '',
      real_phone: '',
      user_id: '',
    });
    // 用户列表（用于关联订单）
    const userList = ref([]);

    // 获取订单列表
    const fetchOrders = async () => {
      loading.value = true;
      try {
        const response = await request({
          url: '/manage/luky-cloud-orders',
          method: 'get',
          params: {
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            process_status: filterForm.processStatus,
            verification_status: filterForm.verificationStatus,
            search: filterForm.keyword,
          },
        });
        orderList.value = response.data.data || [];
        pagination.total = response.data.total || 0;
      } catch (error) {
        ElMessage.error('获取订单列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 获取用户列表
    const fetchUsers = async () => {
      try {
        const response = await request({
          url: '/manage/users',
          method: 'get',
          params: {
            page: 1,
            pageSize: 100,
          },
        });
        userList.value = response.data.data || [];
      } catch (error) {
        console.error('获取用户列表失败:', error);
      }
    };

    // 搜索订单
    const handleSearch = () => {
      pagination.currentPage = 1;
      fetchOrders();
    };

    // 同步订单
    const handleSyncOrders = async (type) => {
      try {
        await request({
          url: '/manage/luky-cloud-orders/sync',
          method: 'post',
          data: {
            type,
          },
        });
        ElMessage.success('订单同步成功');
        fetchOrders();
      } catch (error) {
        ElMessage.error('订单同步失败');
        console.error(error);
      }
    };

    // 处理订单
    const handleProcessOrder = (row) => {
      processForm.id = row.id;
      processForm.order_no = row.order_no;
      processForm.virtual_phone = row.virtual_phone;
      processForm.real_phone = row.real_phone || '';
      processForm.user_id = row.user_id || '';
      processDialogVisible.value = true;
      fetchUsers();
    };

    // 提交处理订单
    const submitProcessOrder = async () => {
      try {
        await request({
          url: `/manage/luky-cloud-orders/${processForm.id}`,
          method: 'put',
          data: {
            real_phone: processForm.real_phone,
            user_id: processForm.user_id,
          },
        });
        ElMessage.success('订单处理成功');
        processDialogVisible.value = false;
        fetchOrders();
      } catch (error) {
        ElMessage.error('订单处理失败');
        console.error(error);
      }
    };

    // 查看订单详情
    const handleViewDetails = () => {
      ElMessage.info('查看详情功能开发中');
    };

    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      fetchOrders();
    };

    // 当前页变化
    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      fetchOrders();
    };

    // 组件挂载时加载数据
    onMounted(() => {
      fetchOrders();
    });

    return {
      loading,
      orderList,
      pagination,
      filterForm,
      processDialogVisible,
      processForm,
      userList,
      handleSearch,
      handleSyncOrders,
      handleProcessOrder,
      submitProcessOrder,
      handleViewDetails,
      handleSizeChange,
      handleCurrentChange,
    };
  },
};
</script>

<style scoped>
.lukeyun-order-list {
  padding: 20px;
}

.lukeyun-order-list h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-buttons {
  display: flex;
  gap: 10px;
}

.order-list-content {
  margin-top: 20px;
}

.filter-area {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input {
  width: 300px;
}

.order-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>
